import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useUpdateUserMutation } from "../../../../../api/userApi";
import { UserProfile } from "@nicsmith/shared_types";
import i18n from "i18next";
import AccountSecurity from "../../components/account-security";
import UserAvatar from "../../components/user-avatar";
import classNames from "classnames/bind";
import styles from "./profile-page.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../../store";
import { togglePopup } from "../../../../../slices/utilsSlice";
import { addToast } from "../../../../../slices/toastSlice";
import { setSession } from "../../../../../slices/sessionSlice";
import UpgradeCard from "../../../../common/upgrade-card/UpgradeCard";
import CustomSelect from "../../../../common/custom-select/CustomSelect";


const cx = classNames.bind(styles);

const LEVEL_OPTIONS = ["A1", "A2", "B1", "B2", "C1", "C2"] as const;
type LevelOption = (typeof LEVEL_OPTIONS)[number];

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.session.user);
  const idToken = useSelector((state: RootState) => state.session.idToken);
  const isAdmin = location.pathname.startsWith("/admin");
  const [updateUser, { isLoading }] = useUpdateUserMutation();
  const [showSrsHelp, setShowSrsHelp] = useState(false);

  const resolveFirstName = (u: typeof user) =>
    u?.first_name || u?.name?.split(' ')[0] || '';
  const resolveLastName = (u: typeof user) =>
    u?.last_name || u?.name?.split(' ').slice(1).join(' ') || '';

  const [firstName, setFirstName] = useState(resolveFirstName(user));
  const [lastName, setLastName] = useState(resolveLastName(user));
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [preferredLanguage, setPreferredLanguage] = useState(user?.preferred_language || "en");
  const [level, setLevel] = useState<LevelOption | "">(user?.profile?.level || "");
  const [targetRetention, setTargetRetention] = useState(user?.profile?.target_retention ?? 0.9);

  useEffect(() => {
    setFirstName(resolveFirstName(user));
    setLastName(resolveLastName(user));
    setPreferredLanguage(user?.preferred_language || "en");
    setLevel(user?.profile?.level || "");
    setTargetRetention(user?.profile?.target_retention ?? 0.9);
  }, [user]);

  const handleSaveProfile = async () => {
    let hasError = false;
    if (!firstName.trim()) {
      setFirstNameError(true);
      hasError = true;
    }
    if (!lastName.trim()) {
      setLastNameError(true);
      hasError = true;
    }
    if (hasError) return;

    try {
      const result = await updateUser({
        id: user?.id?.toString() || "",
        data: {
          ...user!,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          name: `${firstName.trim()} ${lastName.trim()}`,
          preferred_language: preferredLanguage,
          profile: {
            ...user?.profile,
            level: (level || null) as UserProfile["level"],
            target_retention: targetRetention,
          },
        },
      }).unwrap();

      // Sync Redux session with updated user data
      if (result && idToken) {
        dispatch(setSession({ user: result, idToken }));
        // Sync i18n if language changed
        if (result.preferred_language && result.preferred_language !== i18n.language) {
          i18n.changeLanguage(result.preferred_language);
        }
      }

      dispatch(addToast({ type: "success", message: t("profile.updateSuccess") }));
    } catch {
      dispatch(
        togglePopup({
          type: "Error",
          message: t("profile.serverError"),
        })
      );
    }
  };

  return (
    <div className={cx("settingsPage")}>
      <div className={cx("settingsGrid")}>
        {/* Profile Card */}
        <div className={cx("card")}>
          <div className={cx("cardHeader")}>
            <div className={cx("cardIcon", "profile")}>
              <i className="fas fa-user"></i>
            </div>
            <div>
              <h2 className={cx("cardTitle")}>
                {t("settings.profileInfo")}
              </h2>
              <p className={cx("cardDescription")}>
                {t("settings.profileDesc")}
              </p>
            </div>
          </div>

          <div className={cx("cardBody")}>
            {/* Avatar Section */}
            <div className={cx("avatarSection")}>
              <UserAvatar />
            </div>

            {/* First Name */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("settings.firstName")}
              </label>
              <input
                type="text"
                className={cx("input", { inputError: firstNameError })}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setFirstNameError(false);
                }}
                placeholder={t("settings.firstNamePlaceholder")}
              />
              {firstNameError && (
                <span className={cx("errorText")}>
                  {t("settings.firstNameRequired")}
                </span>
              )}
            </div>

            {/* Last Name */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("settings.lastName")}
              </label>
              <input
                type="text"
                className={cx("input", { inputError: lastNameError })}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setLastNameError(false);
                }}
                placeholder={t("settings.lastNamePlaceholder")}
              />
              {lastNameError && (
                <span className={cx("errorText")}>
                  {t("settings.lastNameRequired")}
                </span>
              )}
            </div>

            {/* Email Display */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("accountSecurity.currentEmail")}
              </label>
              <div className={cx("emailDisplay")}>
                <i className="fas fa-envelope"></i>
                <span>{user?.email || "-"}</span>
              </div>
            </div>

            {/* Role Display */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("settings.role")}
              </label>
              <div className={cx("roleBadge", isAdmin ? "admin" : "student")}>
                <i
                  className={`fas ${isAdmin ? "fa-shield-alt" : "fa-graduation-cap"}`}
                ></i>
                <span>{user?.role === "ADMIN" ? t("settings.roleAdmin") : t("settings.roleStudent")}</span>
              </div>
            </div>

            {/* Preferred Language */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("settings.preferredLanguage")}
              </label>
              <CustomSelect
                options={[
                  { value: "en", label: "English" },
                  { value: "de", label: "Deutsch" },
                  { value: "uk", label: "Українська" },
                  { value: "ru", label: "Русский" },
                ]}
                value={preferredLanguage}
                onChange={(val) => {
                  setPreferredLanguage(val);
                  i18n.changeLanguage(val);
                }}
              />
            </div>

            {/* German Level */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel")}>
                {t("settings.germanLevel")}
              </label>
              <div className={cx("levelOptions")}>
                {LEVEL_OPTIONS.map((lvl) => (
                  <button
                    key={lvl}
                    type="button"
                    className={cx("levelOption", { active: level === lvl })}
                    onClick={() => setLevel(lvl)}
                  >
                    <span className={cx("levelCode")}>{lvl}</span>
                    <span className={cx("levelDesc")}>{t(`onboarding.levels.${lvl}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Target Retention (FSRS) */}
            <div className={cx("inputGroup")}>
              <label className={cx("inputLabel", "retentionLabel")}>
                {t("settings.targetRetention", "Target Retention")}
                <button
                  type="button"
                  className={cx("srsHelpBtn")}
                  onClick={() => setShowSrsHelp(true)}
                  title={t("vocab.srsHelpTitle", "How Spaced Repetition Works")}
                >
                  <i className="fas fa-question-circle" />
                </button>
              </label>
              <p className={cx("inputHint")}>
                {t("settings.targetRetentionDesc", "How well you want to remember words. Higher = more frequent reviews.")}
              </p>
              <div className={cx("sliderRow")}>
                <input
                  type="range"
                  className={cx("retentionSlider")}
                  min={70}
                  max={97}
                  step={1}
                  value={Math.round(targetRetention * 100)}
                  onChange={(e) => setTargetRetention(Number(e.target.value) / 100)}
                />
                <span className={cx("retentionValue")}>
                  {Math.round(targetRetention * 100)}%
                </span>
              </div>
            </div>
          </div>

          <div className={cx("cardFooter")}>
            <button
              className={cx("saveButton")}
              onClick={handleSaveProfile}
              disabled={isLoading}
            >
              {isLoading ? (
                <span>{t("common.saving")}</span>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  <span>{t("profile.saveChanges")}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right Column: Security + Subscription */}
        <div className={cx("rightColumn")}>
          <div className={cx("card")}>
            <div className={cx("cardHeader")}>
              <div className={cx("cardIcon", "security")}>
                <i className="fas fa-lock"></i>
              </div>
              <div>
                <h2 className={cx("cardTitle")}>
                  {t("accountSecurity.title")}
                </h2>
                <p className={cx("cardDescription")}>
                  {t("settings.securityDesc")}
                </p>
              </div>
            </div>

            <div className={cx("cardBody")}>
              <AccountSecurity />
            </div>
          </div>

          <UpgradeCard />
        </div>
      </div>
      {/* SRS Help Modal */}
      {showSrsHelp && (
        <div className={cx("srsHelpOverlay")} onClick={() => setShowSrsHelp(false)}>
          <div className={cx("srsHelpModal")} onClick={(e) => e.stopPropagation()}>
            <div className={cx("srsHelpHeader")}>
              <h2>{t("vocab.srsHelpTitle", "How Spaced Repetition Works")}</h2>
              <button className={cx("srsHelpClose")} onClick={() => setShowSrsHelp(false)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className={cx("srsHelpBody")}>
              <p className={cx("srsHelpIntro")}>
                {t("vocab.srsHelpIntro", "Your vocabulary uses the FSRS algorithm — a modern spaced repetition system that adapts to your memory patterns. Here's what each parameter means:")}
              </p>
              <div className={cx("srsHelpGrid")}>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "green")}><i className="fas fa-shield-alt" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpStability", "Stability")}</h4>
                    <p>{t("vocab.srsHelpStabilityDesc", "How long (in days) you can remember this word. Higher stability = longer intervals between reviews.")}</p>
                  </div>
                </div>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "orange")}><i className="fas fa-mountain" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpDifficulty", "Difficulty")}</h4>
                    <p>{t("vocab.srsHelpDifficultyDesc", "How inherently hard this word is for you (0–10). Harder words get reviewed more often.")}</p>
                  </div>
                </div>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "blue")}><i className="fas fa-brain" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpMemory", "Memory %")}</h4>
                    <p>{t("vocab.srsHelpMemoryDesc", "The estimated probability you can recall this word right now. Drops over time until you review.")}</p>
                  </div>
                </div>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "red")}><i className="fas fa-heart-broken" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpLapses", "Lapses")}</h4>
                    <p>{t("vocab.srsHelpLapsesDesc", "How many times you've forgotten this word. More lapses means the word needs more practice.")}</p>
                  </div>
                </div>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "purple")}><i className="fas fa-layer-group" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpStates", "Card States")}</h4>
                    <p>{t("vocab.srsHelpStatesDesc", "New → Learning (short intervals) → Review (days/weeks) → Mastered (21+ day stability). Forgetting sends a card back to Relearning.")}</p>
                  </div>
                </div>
                <div className={cx("srsHelpItem")}>
                  <div className={cx("srsHelpIcon", "teal")}><i className="fas fa-sliders-h" /></div>
                  <div>
                    <h4>{t("vocab.srsHelpRetention", "Target Retention")}</h4>
                    <p>{t("vocab.srsHelpRetentionDesc", "Set in your profile settings (70–97%). Higher = more frequent reviews but better recall. Default is 90%.")}</p>
                  </div>
                </div>
              </div>
              <div className={cx("srsHelpButtons")}>
                <h4>{t("vocab.srsHelpButtonsTitle", "Review Buttons")}</h4>
                <div className={cx("srsHelpButtonList")}>
                  <span className={cx("srsHelpBtnLabel", "again")}>{t("vocab.again", "Again")}</span>
                  <span>{t("vocab.srsHelpBtnAgain", "Didn't remember — card resets to a short interval")}</span>
                  <span className={cx("srsHelpBtnLabel", "hard")}>{t("vocab.hard", "Hard")}</span>
                  <span>{t("vocab.srsHelpBtnHard", "Remembered with difficulty — shorter next interval")}</span>
                  <span className={cx("srsHelpBtnLabel", "good")}>{t("vocab.good", "Good")}</span>
                  <span>{t("vocab.srsHelpBtnGood", "Remembered well — normal next interval")}</span>
                  <span className={cx("srsHelpBtnLabel", "easy")}>{t("vocab.easy", "Easy")}</span>
                  <span>{t("vocab.srsHelpBtnEasy", "Too easy — longer next interval")}</span>
                </div>
              </div>
              <div className={cx("srsHelpTips")}>
                <h4>{t("vocab.srsHelpTipsTitle", "Good to know")}</h4>
                <ul>
                  <li>{t("vocab.srsHelpTip1", "Success Rate counts all your reviews: \"Again\" = incorrect, everything else = correct.")}</li>
                  <li>{t("vocab.srsHelpTip2", "Memory % is based on time since your last review, not on how you answered. Even after pressing \"Again\", it starts high because you just saw the word — but it will drop very quickly.")}</li>
                  <li>{t("vocab.srsHelpTip3", "The system is most useful over time. The more you review, the more accurately it adapts to your memory.")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
